import { Suspense, useContext, useEffect, useRef, useState } from "react";
import {
  Routes,
  Route,
  useNavigationType,
  useLocation,
  Navigate
} from "react-router-dom";
import { navigationPropertiesTypes, useNavigation } from "./routes/Navigation";
import PublicRoute from "./routes/PublicRoute";
import PrivateRoute from "./routes/PrivateRoute";
import DashboardLayout from "./Layout/Dashboard/DashboardLayout";
import AuthLayout from "./Layout/Auth/AuthLayout";
import { isSubscribeDomain, PageData, roles } from "./helper";
import { globalContext } from "./Provider/AppProvider";
import { useVlSupportAlerts } from "./features/SupportTickets/hooks/useVlSupportAlerts";
import PaymentInfo from "./components/shared-components/payment-info/PaymentInfo";
import EmailUnsubscribe from "./components/shared-components/email-unsubscribe/EmailUnsubscribe";
import Auction from "./features/Auction/Auction";
import FaroPageViewTracker from "./components/shared-components/FaroTracker/FaroPageViewTracker";
import { getRoleFromDomain } from "./helper";
import { getStoredIdToken } from "./authToken";
import { ToastContainer } from "react-toastify";

function App() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;
  const previousPathnameRef = useRef(pathname);
  const {handleGlobalState,globalState} = useContext<any>(globalContext)
  const [loading,setLoading]=useState(true);
  const role = getRoleFromDomain();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      document.documentElement.setAttribute('data-theme', savedTheme);
    }
  }, []);
  

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      localStorage.setItem('manageTicketsActiveTab', 'Verification');
      previousPathnameRef.current = pathname;
    }
  }, [pathname]);


  useEffect(() => {
    const baseMetaDescription = role === roles.SUPER_USER ? "QuickAsyst user portal" : "QuickAsyst admin portal";
    const pageData: PageData = {
        "/login": {
            title: "Login",
            metaDescription: `${baseMetaDescription} login page`
        },
        "/forgot-password": {
            title: "Forgot Password",
            metaDescription: `${baseMetaDescription} forgot password`
        },
        "/reset-password": {
            title: "Reset Password",
            metaDescription: `${baseMetaDescription} reset password`
        },
        "/dashboard": {
            title: "Dashboard",
            metaDescription: `${baseMetaDescription} dashboard page`
        },
        "/managetickets": {
            title: "Manage Tickets",
            metaDescription: `${baseMetaDescription} manage tickets page`
        },
        "/support-tickets": {
            title: "Support Tickets",
            metaDescription: `${baseMetaDescription} support tickets page`
        },
        "/tickets": {
            title: "Tickets",
            metaDescription: `${baseMetaDescription} tickets page`
        },
        "/users": {
            title: "Users",
            metaDescription: `${baseMetaDescription} users page`
        },
        "/affiliate": {
            title: "Affiliates",
            metaDescription: `${baseMetaDescription} affiliates page`
        },
        "/profile": {
            title: "Profile",
            metaDescription: `${baseMetaDescription} profile page`
        },
"/settings": {
             title: "Settings",
             metaDescription: `${baseMetaDescription} settings page`
         },
         "/app-version": {
             title: "App Version",
             metaDescription: `${baseMetaDescription} app version page`
         },
         "/app-update": {
             title: "App Update",
             metaDescription: `${baseMetaDescription} app update page`
         },
        "/logs": {
           title: "Logs",
           metaDescription: `${baseMetaDescription} logs page`
        },
         "/buy-ticket-savings": {
            title: "Ticket Savings",
            metaDescription: `${baseMetaDescription} buy-side ticket savings page`
        },
         "/auction": {
            title: "Live Auction",
            metaDescription: "QuickAsyst live ticket auction — World Cup Final"
        }
    };

    const pageInfo = pageData[pathname];
    if (pageInfo) {
        if (pathname === '/auction') {
            document.title = `QuickAsyst — ${pageInfo.title}: World Cup Final`;
        } else {
            const Title = role === roles.SUPER_USER ? 'QuickAsyst User Portal':'QuickAsyst Admin Portal';
            document.title = `${Title} - ${pageInfo.title}`;
        }

        const metaDescriptionTag = document.querySelector('head > meta[name="description"]');
        if (metaDescriptionTag) {
            metaDescriptionTag.setAttribute("content", pageInfo.metaDescription);
        }
    }
}, [pathname]);

  const {navigation} = useNavigation();
  const isAuthenticated = getStoredIdToken();
  // Victory Live support replies: sidebar badge, chime and toast for everyone logged in. It lives here, not
  // in the per-page layout, because each page change remounts that layout and would drop the live connection.
  useVlSupportAlerts(Boolean(isAuthenticated && globalState?.profile?.userRole));
  const defaultPrivatePath = navigation?.private?.find((route) => route.path !== "/no-access")?.path || "/no-access";
  
  return (
    
    <Suspense>   
      <FaroPageViewTracker />
         <ToastContainer
        position="top-center"
        autoClose={3000}
        limit={1}
        newestOnTop
      />
      <Routes>
      
        <Route path="/" element={<Navigate to='/login'/>}/>
        {/* mapping public routes */}
        {
          navigation?.public?.map((route: navigationPropertiesTypes)=>(
            <Route 
              path={route.path}
              key={route.path}
              element={
                <PublicRoute>
                  {
                    route.path === '/paymentinfo' ? 
                    <PaymentInfo/>
                    : route.path === '/unsubscribe' ? <EmailUnsubscribe/>
                    : route.path === '/auction' ? <Auction/>
                    : <AuthLayout component={route.component} role={role}/>
                  }
                </PublicRoute>
              }
            />
          ))
        }
        {/* mapping private routes */}
        {
          navigation?.private?.map((route: navigationPropertiesTypes)=>(
            <Route
            path={route.path}
            key={route.path}
            element={
              <PrivateRoute>
                <DashboardLayout component={route.component}/>
              </PrivateRoute>
            }
            />
          ))
        }
        <Route path="*" element={
          isSubscribeDomain()
            ? <Navigate to='/unsubscribe'/>
            : isAuthenticated
              ? <Navigate to={defaultPrivatePath}/>
              : <Navigate to='/login'/>
        }/>
      </Routes>
    </Suspense> 
  );
}
export default App;
